import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { studentsAPI } from '../../services/api';
import { normalizeApiErrorMessage } from '../../utils/apiErrorMessage';
import { toast } from 'react-toastify';
import { getDocumentFileSizeError } from '../../utils/uploadLimits';
import { Country, State, City } from 'country-state-city';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { parsePhoneNumber } from 'libphonenumber-js';
import {
  isValidPassportNumber,
  PASSPORT_FORMAT_MESSAGE,
  normalizePassportInput
} from '../../utils/passportValidation';
import brandLogo from '../../assets/instabizweb.jpeg';

const defaultStudiesFollowed = [
  { level: 'High School', year: '', establishment: '', degree_title: '', comments: '' },
  { level: 'Bachelor', year: '', establishment: '', degree_title: '', comments: '' },
  { level: 'Mastere', year: '', establishment: '', degree_title: '', comments: '' },
  { level: 'Other', year: '', establishment: '', degree_title: '', comments: '' }
];
const defaultLanguages = [
  { language: '', written: '', oral: '', duration: '' },
  { language: '', written: '', oral: '', duration: '' },
  { language: '', written: '', oral: '', duration: '' }
];
const defaultStaysAbroad = [
  { country: '', duration: '', year: '', activities: '' },
  { country: '', duration: '', year: '', activities: '' },
  { country: '', duration: '', year: '', activities: '' },
  { country: '', duration: '', year: '', activities: '' }
];
const defaultWorkInternships = [
  { company: '', duration_year: '', missions: '' },
  { company: '', duration_year: '', missions: '' },
  { company: '', duration_year: '', missions: '' },
  { company: '', duration_year: '', missions: '' },
  { company: '', duration_year: '', missions: '' }
];
const defaultCulturalActivities = [
  { activity: '', years: '', level: '' },
  { activity: '', years: '', level: '' },
  { activity: '', years: '', level: '' },
  { activity: '', years: '', level: '' },
  { activity: '', years: '', level: '' }
];

const defaultForm = {
  title: 'Mr.',
  first_name: '',
  last_name: '',
  dob: '',
  address: '',
  city: '',
  state: '',
  country: '',
  zip_code: '',
  phone_country_code: '+91',
  phone: '',
  email: '',
  passport_no: '',
  gender: '',
  language: 'ENGLISH',
  current_degree: '',
  source: '',
  intake_id: '',
  course_id: '',
  campus_id: '',
  program_type: '',
  formation_type: '',
  internship_program: '',
  status_desired: '',
  business_specialization: '',
  social_security_number: '',
  parent_address: '',
  parent_zip_code: '',
  parent_city: '',
  parent_state: '',
  parent_country: '',
  parent_location_country_code: '',
  parent_location_state_code: '',
  father_phone: '',
  mother_phone: '',
  is_disabled: '',
  how_found_school: '',
  it_knowledge: '',
  org_type: '',
  org_type_other: '',
  org_town: '',
  org_department: '',
  org_advisor_name: '',
  org_advisor_email: '',
  org_advisor_phone: '',
  org_advisor_phone_country_code: '',
  org_registered_france_travail: '',
  org_registration_date: '',
  org_identification_number: '',
  org_compensated_france_travail: '',
  org_compensated_until_date: '',
  studies_followed: defaultStudiesFollowed,
  modern_languages: defaultLanguages,
  stays_abroad: defaultStaysAbroad,
  work_internships: defaultWorkInternships,
  cultural_activities: defaultCulturalActivities,
  location_country_code: '',
  location_state_code: ''
};

export default function StudentIntakeForm() {
  const { t } = useTranslation();
  const { token } = useParams();
  const location = useLocation();
  const [uiLang, setUiLang] = useState('en');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** When meta fails: 'used' = one-time link already consumed (410), otherwise invalid/inactive */
  const [metaErrorKind, setMetaErrorKind] = useState(null);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [done, setDone] = useState(null);
  const [requiredDocFiles, setRequiredDocFiles] = useState({});
  const [requiredDocVerification, setRequiredDocVerification] = useState({});
  const [requiredDocVerifying, setRequiredDocVerifying] = useState({});
  const [phoneE164, setPhoneE164] = useState('');
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const tx = (en, fr) => (uiLang === 'fr' ? fr : en);
  const lng = uiLang === 'fr' ? 'fr' : 'en';

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(
    () => (form.location_country_code ? State.getStatesOfCountry(form.location_country_code) : []),
    [form.location_country_code]
  );
  const cities = useMemo(
    () => (form.location_country_code && form.location_state_code
      ? City.getCitiesOfState(form.location_country_code, form.location_state_code)
      : []),
    [form.location_country_code, form.location_state_code]
  );
  const parentStates = useMemo(
    () => (form.parent_location_country_code ? State.getStatesOfCountry(form.parent_location_country_code) : []),
    [form.parent_location_country_code]
  );
  const parentCities = useMemo(
    () => (form.parent_location_country_code && form.parent_location_state_code
      ? City.getCitiesOfState(form.parent_location_country_code, form.parent_location_state_code)
      : []),
    [form.parent_location_country_code, form.parent_location_state_code]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMetaErrorKind(null);
    studentsAPI.getIntakeFormMeta(token)
      .then((res) => {
        if (cancelled) return;
        setMetaErrorKind(null);
        const m = res.data || {};
        const queryLang = new URLSearchParams(location.search).get('lang');
        const normalizedQueryLang = String(queryLang || '').toLowerCase();
        const normalizedMetaLang = String(m.form_language || '').toLowerCase();
        const effectiveLang = ['fr', 'en'].includes(normalizedQueryLang)
          ? normalizedQueryLang
          : (['fr', 'en'].includes(normalizedMetaLang) ? normalizedMetaLang : 'en');
        setUiLang(effectiveLang);
        setMeta(m);
        setForm((f) => ({
          ...f,
          country: String(m.country_code || '').toUpperCase() === 'FRANCE' ? 'France' : 'India',
          phone_country_code: String(m.country_code || '').toUpperCase() === 'FRANCE' ? '+33' : '+91',
          language: String(m.country_code || '').toUpperCase() === 'FRANCE'
            ? (effectiveLang === 'fr' ? 'FRENCH' : 'ENGLISH')
            : 'ENGLISH',
          location_country_code: String(m.country_code || '').toUpperCase() === 'FRANCE' ? 'FR' : 'IN',
          location_state_code: ''
        }));
        document.documentElement.setAttribute('lang', effectiveLang);
        setPhoneE164('');
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        const code = err.response?.data?.code;
        setMeta(null);
        if (status === 410 || code === 'LINK_ALREADY_USED') {
          setMetaErrorKind('used');
        } else {
          setMetaErrorKind('invalid');
          toast.error(
            normalizeApiErrorMessage(err, tx('Invalid or inactive link', 'Le lien est invalide ou inactif.'))
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, location.search]);

  const isFrance = String(meta?.country_code || '').toUpperCase() === 'FRANCE';
  // Whether the STUDENT's own country (selected in the form) is France.
  // Used to gate France-specific sections (e.g. France Travail follow-up) so
  // they only appear when the student actually resides in France.
  const isFranceStudent = (
    String(form.location_country_code || '').toUpperCase() === 'FR'
    || String(form.country || '').trim().toLowerCase() === 'france'
  );
  const requiredDocuments = useMemo(() => (meta?.required_documents || []), [meta]);
  const requiredDocumentDetails = useMemo(
    () => (Array.isArray(meta?.required_document_details) ? meta.required_document_details : []),
    [meta]
  );
  const passportDocKeys = useMemo(
    () => {
      if (requiredDocumentDetails.length > 0) {
        return requiredDocumentDetails
          .filter((d) => Number(d?.is_passport || 0) === 1 || d?.is_passport === true)
          .map((d) => String(d?.document_type_name || '').trim())
          .filter(Boolean);
      }
      // Backward compatibility: fallback when old meta payload does not include document details.
      return (requiredDocuments || [])
        .filter((d) => /passport|passeport/i.test(String(d || '')))
        .map((d) => String(d));
    },
    [requiredDocuments, requiredDocumentDetails]
  );
  const hasUnverifiedSelectedPassport = useMemo(
    () => passportDocKeys.some((k) => requiredDocFiles[k] && requiredDocVerification[k] !== 'verified'),
    [passportDocKeys, requiredDocFiles, requiredDocVerification]
  );
  const filteredProgramTypes = useMemo(() => {
    const campusIdNum = Number(form.campus_id || 0);
    if (!campusIdNum) return [];
    const allowedCourseIds = new Set(
      (meta?.course_campus_fees || [])
        .filter((m) => Number(m.campus_id) === campusIdNum)
        .map((m) => Number(m.course_id))
    );
    if (!allowedCourseIds.size) return [];
    const usedTypes = new Set(
      (meta?.courses || [])
        .filter((c) => allowedCourseIds.has(Number(c.course_id)))
        .map((c) => String(c.program_type || '').trim())
        .filter(Boolean)
    );
    return (meta?.program_types || []).filter((p) => usedTypes.has(String(p || '').trim()));
  }, [form.campus_id, meta]);
  const filteredCourses = useMemo(() => {
    const campusIdNum = Number(form.campus_id || 0);
    if (!campusIdNum || !form.program_type) return [];
    const allowedCourseIds = new Set(
      (meta?.course_campus_fees || [])
        .filter((m) => Number(m.campus_id) === campusIdNum)
        .map((m) => Number(m.course_id))
    );
    return (meta?.courses || []).filter(
      (c) => allowedCourseIds.has(Number(c.course_id)) && String(c.program_type || '') === String(form.program_type || '')
    );
  }, [form.campus_id, form.program_type, meta]);
  const missingRequiredDocs = useMemo(
    () => requiredDocuments.filter((docName) => !requiredDocFiles[String(docName)]),
    [requiredDocuments, requiredDocFiles]
  );
  const missingAcademicSelection = useMemo(
    () => !form.intake_id || !form.campus_id || !form.program_type || !form.course_id,
    [form.intake_id, form.campus_id, form.program_type, form.course_id]
  );
  const submitDisabled = useMemo(() => (
    !form.first_name ||
    !form.last_name ||
    !phoneE164 ||
    !form.email ||
    !form.passport_no ||
    !isValidPassportNumber(form.passport_no) ||
    !form.language ||
    (isFrance && !form.formation_type) ||
    missingRequiredDocs.length > 0 ||
    hasUnverifiedSelectedPassport ||
    missingAcademicSelection
  ), [form, phoneE164, isFrance, missingRequiredDocs.length, hasUnverifiedSelectedPassport, missingAcademicSelection]);
  const verifyPassportImmediately = async (docType, file) => {
    if (!isValidPassportNumber(form.passport_no)) {
      console.log('[Passport Verify][IntakeForm] Skipped: invalid passport number before API call', {
        document_type: docType,
        passport_no: form.passport_no || null
      });
      setRequiredDocVerification((prev) => ({ ...prev, [docType]: 'not_verified' }));
      return;
    }
    const fd = new FormData();
    fd.append('document_type', String(docType || ''));
    fd.append('passport_no', String(form.passport_no || ''));
    fd.append('file', file);
    console.log('[Passport Verify][IntakeForm] Selected file:', {
      document_type: docType,
      file_name: file?.name,
      file_type: file?.type,
      file_size: file?.size
    });
    setRequiredDocVerifying((prev) => ({ ...prev, [docType]: true }));
    try {
      const res = await studentsAPI.verifyIntakePassportDocument(token, fd);
      const verified = !!res?.data?.verified;
      console.log('[Passport Verify][IntakeForm] API response:', res?.data);
      console.log('[Passport Verify][IntakeForm] Extracted passport number:', res?.data?.extracted_passport_no || null);
      setRequiredDocVerification((prev) => ({ ...prev, [docType]: verified ? 'verified' : 'not_verified' }));
    } catch (err) {
      console.log('[Passport Verify][IntakeForm] API error response:', err?.response?.data || null);
      setRequiredDocVerification((prev) => ({ ...prev, [docType]: 'not_verified' }));
    } finally {
      setRequiredDocVerifying((prev) => ({ ...prev, [docType]: false }));
    }
  };


  useEffect(() => {
    if (submissionAttempted && !submitDisabled) setSubmissionAttempted(false);
  }, [submissionAttempted, submitDisabled]);

  useEffect(() => {
    if (!passportDocKeys.length) return;
    setRequiredDocVerification((prev) => {
      const next = { ...prev };
      passportDocKeys.forEach((k) => { next[k] = ''; });
      return next;
    });
  }, [form.passport_no, passportDocKeys]);

  const splitPhoneForApi = (e164) => {
    if (!e164) return { phone_country_code: form.phone_country_code || '+91', phone: '' };
    try {
      const parsed = parsePhoneNumber(e164);
      if (parsed?.countryCallingCode && parsed?.nationalNumber) {
        return { phone_country_code: `+${parsed.countryCallingCode}`, phone: parsed.nationalNumber };
      }
    } catch (_) {
      // fallback below
    }
    return { phone_country_code: form.phone_country_code || '+91', phone: String(e164).replace(/^\+/, '') };
  };

  const handlePhoneChange = (value) => {
    const e164 = value || '';
    setPhoneE164(e164);
    const parts = splitPhoneForApi(e164);
    setForm((f) => ({ ...f, phone_country_code: parts.phone_country_code, phone: parts.phone }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionAttempted(true);
    if (submitDisabled) {
      if (missingAcademicSelection) {
        toast.error(t('students.intakeMissingAcademic', { lng }));
      }
      if (missingRequiredDocs.length > 0) {
        const list = missingRequiredDocs.map((d) => String(d)).join(', ');
        toast.error(t('students.intakeMissingDocumentsList', { lng, list }));
      }
      if (!missingAcademicSelection && missingRequiredDocs.length === 0) {
        if (form.passport_no && !isValidPassportNumber(form.passport_no)) {
          toast.error(PASSPORT_FORMAT_MESSAGE);
        } else {
          toast.error(t('students.intakeFillRequiredFields', { lng }));
        }
      }
      return;
    }
    try {
      setSaving(true);
      const normalizedFirstName = String(form.first_name || '').trim().replace(/\s+/g, ' ');
      const normalizedLastName = String(form.last_name || '').trim().replace(/\s+/g, ' ');
      const phoneParts = splitPhoneForApi(phoneE164);
      const payload = {
        ...form,
        first_name: normalizedFirstName
          ? normalizedFirstName.charAt(0).toUpperCase() + normalizedFirstName.slice(1).toLowerCase()
          : '',
        last_name: normalizedLastName ? normalizedLastName.toUpperCase() : '',
        phone: phoneParts.phone,
        phone_country_code: phoneParts.phone_country_code,
        intake_id: form.intake_id || null,
        course_id: form.course_id || null,
        campus_id: form.campus_id || null,
        source: form.source || null
      };
      const { data } = await studentsAPI.submitIntakeForm(token, payload);

      const studentId = data?.student_id;
      // Align with server `required_documents` order and names so each file maps to the correct type.
      const docPairs = (requiredDocuments || [])
        .map((docName) => {
          const key = String(docName);
          const file = requiredDocFiles[key];
          return file ? { documentType: key, file } : null;
        })
        .filter(Boolean);
      if (studentId && docPairs.length > 0) {
        const fd = new FormData();
        fd.append('student_id', String(studentId));
        fd.append('document_types', JSON.stringify(docPairs.map((p) => p.documentType)));
        docPairs.forEach((p) => fd.append('files', p.file));
        try {
          const uploadRes = await studentsAPI.uploadIntakeDocumentsBatch(token, fd);
          const uploaded = Array.isArray(uploadRes?.data?.uploaded) ? uploadRes.data.uploaded : [];
          const nextVerification = {};
          uploaded.forEach((row) => {
            const dt = String(row?.document_type || '').trim();
            if (!dt) return;
            nextVerification[dt] = row?.verification?.verified ? 'verified' : 'not_verified';
          });
          if (Object.keys(nextVerification).length > 0) {
            setRequiredDocVerification((prev) => ({ ...prev, ...nextVerification }));
          }
        } catch (uploadErr) {
          const failedDocTypeMatch = String(uploadErr?.response?.data?.message || '').match(/"([^"]+)"/);
          const failedDocType = failedDocTypeMatch?.[1] || '';
          if (failedDocType) {
            setRequiredDocVerification((prev) => ({ ...prev, [failedDocType]: 'not_verified' }));
          }
          const detail = normalizeApiErrorMessage(uploadErr, t('students.intakeSubmitFailed', { lng }));
          toast.error(`${t('students.intakeDocumentUploadFailed', { lng })} ${detail}`);
          toast.info(t('students.intakeUploadPartialHint', { lng }));
          return;
        }
      }

      setSubmissionAttempted(false);
      setDone(data || { message: tx('Submitted', 'Soumis') });
      toast.success(tx('Form submitted successfully', 'Formulaire soumis avec succes'));
    } catch (err) {
      const status = err.response?.status;
      const code = err.response?.data?.code;
      if (status === 410 || code === 'LINK_ALREADY_USED') {
        toast.error(normalizeApiErrorMessage(err, tx(
          'This registration link has already been used. Please contact your school or agency for a new link.',
          'Ce lien d\'inscription a déjà été utilisé. Veuillez contacter votre école ou votre agence pour obtenir un nouveau lien.'
        )));
      } else {
        toast.error(normalizeApiErrorMessage(err, t('students.intakeSubmitFailed', { lng })));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCountrySelect = (countryCode) => {
    const selected = countries.find((c) => c.isoCode === countryCode);
    setForm((f) => ({
      ...f,
      location_country_code: countryCode,
      location_state_code: '',
      country: selected?.name || '',
      state: '',
      city: ''
    }));
  };

  const handleStateSelect = (stateCode) => {
    const selectedState = states.find((s) => s.isoCode === stateCode);
    setForm((f) => ({
      ...f,
      location_state_code: stateCode,
      state: selectedState?.name || '',
      city: ''
    }));
  };

  if (loading) {
    return <div className="loading-spinner"><div className="spinner" /></div>;
  }
  if (metaErrorKind === 'used') {
    const title = tx('Link already used', 'Lien déjà utilisé');
    const body = tx(
      'This registration link can only be used once and has already been submitted. Please contact your school or agency if you need a new link.',
      'Ce lien d\'inscription est à usage unique et a déjà été envoyé. Contactez votre école ou votre agence si vous avez besoin d\'un nouveau lien.'
    );
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', padding: 24, textAlign: 'center' }}>
        <div className="card" style={{ padding: 28 }}>
          <h1 style={{ marginTop: 0, fontSize: 22 }}>{title}</h1>
          <p style={{ color: 'var(--text-muted, #64748b)', lineHeight: 1.6 }}>{body}</p>
        </div>
      </div>
    );
  }
  if (!meta) {
    return <div style={{ padding: 24, textAlign: 'center' }}>{tx('Link is invalid or inactive.', 'Le lien est invalide ou inactif.')}</div>;
  }
  if (done) {
    return (
      <div style={{ maxWidth: 760, margin: '24px auto', padding: 20 }}>
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{tx('Thank you', 'Merci')}</h2>
          <p>{done.message || 'Student submitted successfully'}</p>
          {done.student_ref ? <p><strong>{tx('Student Ref:', 'Reference etudiant :')}</strong> {done.student_ref}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 980, margin: '24px auto', padding: '0 12px' }}>
      <div
        className="card"
        style={{
          padding: 0,
          overflow: 'hidden',
          borderRadius: 16
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
            borderBottom: '1px solid var(--border-color)',
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap'
          }}
        >
          <img
            src={brandLogo}
            alt="Logo"
            style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 10, background: '#fff', padding: 4, border: '1px solid #e5e7eb' }}
          />
          <div>
            <h2 style={{ margin: 0, lineHeight: 1.2 }}>{tx('Student Admission Form', 'Formulaire d admission etudiant')}</h2>
            <div style={{ marginTop: 4, color: 'var(--text-muted)', fontSize: 13 }}>
              {tx('Please complete all required details to submit your registration.', 'Veuillez completer tous les champs obligatoires pour soumettre votre inscription.')}
            </div>
          </div>
        </div>

        <div style={{ padding: 20 }}>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group"><label>{tx('Title', 'Civilite')}</label>
              <select value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}>
                <option value="Mr.">{tx('Mr.', 'M.')}</option>
                <option value="Mrs.">{tx('Mrs.', 'Mme')}</option>
                <option value="Ms.">{tx('Ms.', 'Mlle')}</option>
                <option value="Dr.">{tx('Dr.', 'Dr.')}</option>
              </select>
            </div>
            <div className="form-group"><label>{tx('First Name *', 'Prenom *')}</label><input value={form.first_name} onChange={(e) => setForm((f) => {
              const raw = String(e.target.value || '');
              const normalized = raw.replace(/\s+/g, ' ');
              const next = normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase() : '';
              return { ...f, first_name: next };
            })} /></div>
            <div className="form-group"><label>{tx('Last Name *', 'Nom *')}</label><input value={form.last_name} onChange={(e) => setForm((f) => {
              const raw = String(e.target.value || '');
              return { ...f, last_name: raw.toUpperCase() };
            })} /></div>
            <div className="form-group"><label>{tx('Date of Birth', 'Date de naissance')}</label><input type="date" value={form.dob} onChange={(e) => setForm((f) => ({ ...f, dob: e.target.value }))} /></div>
            <div className="form-group">
              <label>{tx('Phone *', 'Telephone *')}</label>
              <PhoneInput
                international
                defaultCountry={String(meta?.country_code || '').toUpperCase() === 'FRANCE' ? 'FR' : 'IN'}
                value={phoneE164 || undefined}
                onChange={handlePhoneChange}
                placeholder={tx('Enter phone number', 'Entrez le numero de telephone')}
              />
            </div>
            <div className="form-group"><label>{tx('Email *', 'Email *')}</label><input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div className="form-group"><label>{isFrance ? t('students.intakePassportNumberFrance', { lng }) : tx('Passport No *', 'Numéro de passeport *')}</label><input value={form.passport_no} onChange={(e) => setForm((f) => ({ ...f, passport_no: normalizePassportInput(e.target.value) }))} />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                {t('students.intakePassportUploadHint', { lng })}
              </p>
              {form.passport_no && !isValidPassportNumber(form.passport_no) ? (
                <div className="error-text">{PASSPORT_FORMAT_MESSAGE}</div>
              ) : null}
            </div>
            <div className="form-group"><label>{tx('Gender', 'Genre')}</label>
              <select value={form.gender} onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}>
                <option value="">{tx('Select', 'Selectionner')}</option><option value="Male">{tx('Male', 'Homme')}</option><option value="Female">{tx('Female', 'Femme')}</option><option value="Other">{tx('Other', 'Autre')}</option>
              </select>
            </div>
            <div className="form-group"><label>{tx('Language *', 'Langue *')}</label>
              <select value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}>
                <option value="ENGLISH">{tx('English', 'Anglais')}</option>
                <option value="FRENCH">{tx('French', 'Francais')}</option>
              </select>
            </div>
            {isFrance && (
              <div className="form-group"><label>{tx('Formation Type *', 'Type de formation *')}</label>
                <select value={form.formation_type} onChange={(e) => setForm((f) => ({ ...f, formation_type: e.target.value }))}>
                  <option value="">{tx('Select', 'Selectionner')}</option>
                  <option value="initial">{tx('Initial', 'Initiale')}</option>
                  <option value="alternance">{tx('Alternance', 'Alternance')}</option>
                </select>
              </div>
            )}
            {isFrance && (
              <div className="form-group"><label>Internship Program</label>
                <select value={form.internship_program} onChange={(e) => setForm((f) => ({ ...f, internship_program: e.target.value }))}>
                  <option value="">{tx('Select', 'Selectionner')}</option>
                  {(meta.internship_programs || []).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}
            {isFrance && (
              <div className="form-group"><label>Status Desired</label>
                <select value={form.status_desired} onChange={(e) => setForm((f) => ({ ...f, status_desired: e.target.value }))}>
                  <option value="">{tx('Select', 'Selectionner')}</option>
                  {(meta.status_desired_options || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            )}
            {isFrance && (
              <div className="form-group"><label>Business Specialization</label>
                <select value={form.business_specialization} onChange={(e) => setForm((f) => ({ ...f, business_specialization: e.target.value }))}>
                  <option value="">{tx('Select', 'Selectionner')}</option>
                  {(meta.business_specializations || []).map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            )}
            {isFrance && (
              <div className="form-group"><label>Social Security Number</label>
                <input value={form.social_security_number} onChange={(e) => setForm((f) => ({ ...f, social_security_number: e.target.value }))} />
              </div>
            )}
            {isFrance && (
              <div className="form-group"><label>Are You Disabled?</label>
                <select value={form.is_disabled} onChange={(e) => setForm((f) => ({ ...f, is_disabled: e.target.value }))}>
                  <option value="">{tx('Select', 'Selectionner')}</option>
                  <option value="Yes">{tx('Yes', 'Oui')}</option>
                  <option value="No">{tx('No', 'Non')}</option>
                </select>
              </div>
            )}
            {isFrance && (
              <div className="form-group"><label>{tx('How Did You Find Out About the School?', 'Comment avez-vous connu l ecole ?')}</label>
                <select value={form.how_found_school} onChange={(e) => setForm((f) => ({ ...f, how_found_school: e.target.value }))}>
                  <option value="">{tx('Select', 'Selectionner')}</option>
                  {(meta.how_found_options || []).map((o) => {
                    const name = typeof o === 'string' ? o : o.option_name;
                    const label = typeof o === 'string'
                      ? o
                      : (uiLang === 'fr' ? (o.option_name_fr || o.option_name) : o.option_name);
                    return <option key={name} value={name}>{label}</option>;
                  })}
                </select>
              </div>
            )}
            {isFrance && (
              <div className="form-group full-width"><label>IT Knowledge</label>
                <input value={form.it_knowledge} onChange={(e) => setForm((f) => ({ ...f, it_knowledge: e.target.value }))} placeholder="e.g. Word, Excel, PowerPoint..." />
              </div>
            )}
            <div className="form-group"><label>{tx('Current Degree', 'Diplome actuel')}</label><input value={form.current_degree} onChange={(e) => setForm((f) => ({ ...f, current_degree: e.target.value }))} /></div>
            <div className="form-group"><label>{tx('Source', 'Source')}</label>
              <select value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
                <option value="">{tx('Select', 'Selectionner')}</option>
                {(meta.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tx('Intake', 'Rentree')}</label>
              <select value={form.intake_id} onChange={(e) => setForm((f) => ({ ...f, intake_id: e.target.value }))}>
                <option value="">{tx('Select', 'Selectionner')}</option>
                {(meta.intakes || []).map((i) => <option key={i.intake_id} value={i.intake_id}>{i.intake_name} {i.year || ''}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tx('Campus', 'Campus')}</label>
              <select
                value={form.campus_id}
                onChange={(e) => setForm((f) => ({ ...f, campus_id: e.target.value, program_type: '', course_id: '' }))}
              >
                <option value="">{tx('Select', 'Selectionner')}</option>
                {(meta.campuses || []).map((c) => <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tx('Program Type', 'Type de programme')}</label>
              <select
                value={form.program_type}
                onChange={(e) => setForm((f) => ({ ...f, program_type: e.target.value, course_id: '' }))}
                disabled={!form.campus_id}
              >
                <option value="">{form.campus_id ? tx('Select Program', 'Selectionner un programme') : tx('Select Campus First', 'Selectionnez d abord un campus')}</option>
                {filteredProgramTypes.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tx('Course', 'Formation')}</label>
              <select
                value={form.course_id}
                onChange={(e) => setForm((f) => ({ ...f, course_id: e.target.value }))}
                disabled={!form.campus_id || !form.program_type}
              >
                <option value="">
                  {!form.campus_id ? tx('Select Campus First', 'Selectionnez d abord un campus') : form.program_type ? tx('Select Course', 'Selectionner la formation') : tx('Select Program First', 'Selectionnez d abord un programme')}
                </option>
                {filteredCourses.map((c) => <option key={c.course_id} value={c.course_id}>{c.course_name}</option>)}
              </select>
            </div>
            <div className="form-group full-width"><label>{tx('Address', 'Adresse')}</label><textarea rows={3} value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} /></div>
            <div className="form-group"><label>{tx('Country', 'Pays')}</label>
              <select value={form.location_country_code || ''} onChange={(e) => handleCountrySelect(e.target.value)}>
                <option value="">{tx('Select Country', 'Selectionner le pays')}</option>
                {countries.map((c) => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tx('State', 'Region')}</label>
              <select
                value={form.location_state_code || ''}
                onChange={(e) => handleStateSelect(e.target.value)}
                disabled={!form.location_country_code || states.length === 0}
              >
                <option value="">{states.length ? tx('Select State', 'Selectionner la region') : tx('Select Country First', 'Selectionnez d abord le pays')}</option>
                {states.map((s) => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tx('City', 'Ville')}</label>
              <select
                value={form.city || ''}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                disabled={!form.location_state_code || cities.length === 0}
              >
                <option value="">{cities.length ? tx('Select City', 'Selectionner la ville') : tx('Select State First', 'Selectionnez d abord la region')}</option>
                {cities.map((c) => <option key={`${c.name}-${c.stateCode || ''}`} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tx('Zip Code', 'Code postal')}</label><input value={form.zip_code} onChange={(e) => setForm((f) => ({ ...f, zip_code: e.target.value }))} /></div>

            {/* France-only: Parent Information */}
            {isFrance && (
              <>
                <div className="form-group full-width" style={{ marginTop: 16 }}>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>Parents' Information</h4>
                </div>
                <div className="form-group full-width"><label>Parents' Address</label>
                  <input value={form.parent_address} onChange={(e) => setForm((f) => ({ ...f, parent_address: e.target.value }))} />
                </div>
                <div className="form-group"><label>Country (Parents)</label>
                  <select
                    value={form.parent_location_country_code || ''}
                    onChange={(e) => {
                      const code = e.target.value;
                      const selected = countries.find(c => c.isoCode === code);
                      setForm(f => ({ ...f, parent_location_country_code: code, parent_country: selected?.name || '', parent_location_state_code: '', parent_state: '', parent_city: '' }));
                    }}
                  >
                    <option value="">{tx('Select Country', 'Selectionner le pays')}</option>
                    {countries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>State (Parents)</label>
                  <select
                    value={form.parent_location_state_code || ''}
                    onChange={(e) => {
                      const code = e.target.value;
                      const selected = parentStates.find(s => s.isoCode === code);
                      setForm(f => ({ ...f, parent_location_state_code: code, parent_state: selected?.name || '', parent_city: '' }));
                    }}
                    disabled={!form.parent_location_country_code || parentStates.length === 0}
                  >
                    <option value="">{parentStates.length ? tx('Select State', 'Selectionner la region') : tx('Select Country First', 'Selectionnez d abord le pays')}</option>
                    {parentStates.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>City (Parents)</label>
                  {!form.parent_location_state_code ? (
                    <input type="text" disabled value="" placeholder={tx('Select State First', 'Selectionnez d abord la region')} />
                  ) : parentCities.length > 0 ? (
                    <select value={form.parent_city || ''} onChange={(e) => setForm(f => ({ ...f, parent_city: e.target.value }))}>
                      <option value="">{tx('Select City', 'Selectionner la ville')}</option>
                      {parentCities.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                  ) : (
                    <input value={form.parent_city || ''} onChange={(e) => setForm(f => ({ ...f, parent_city: e.target.value }))} placeholder="Enter city" />
                  )}
                </div>
                <div className="form-group"><label>Zip Code (Parents)</label><input value={form.parent_zip_code} onChange={(e) => setForm((f) => ({ ...f, parent_zip_code: e.target.value }))} /></div>
                <div className="form-group"><label>Father's Mobile Phone</label><input value={form.father_phone} onChange={(e) => setForm((f) => ({ ...f, father_phone: e.target.value }))} placeholder="+33..." /></div>
                <div className="form-group"><label>Mother's Mobile Phone</label><input value={form.mother_phone} onChange={(e) => setForm((f) => ({ ...f, mother_phone: e.target.value }))} placeholder="+33..." /></div>
              </>
            )}

            {/* France-only (by student country): Organization follow-up (France Travail / Mission Locale) */}
            {isFranceStudent && (
              <>
                <div className="form-group full-width" style={{ marginTop: 16 }}>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>
                    {tx('If you are followed by an organization', 'Si vous êtes suivi(e) par un organisme')}
                  </h4>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                    {tx('France Travail, Local Mission, etc.', 'France Travail, mission locale, etc.')}
                  </p>
                </div>
                <div className="form-group"><label>{tx('France Travail / Local Mission / Other', 'France Travail / Mission Locale / Autre')}</label>
                  <select
                    value={form.org_type}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, org_type: v, org_type_other: v === 'Other' ? f.org_type_other : '' }));
                    }}
                  >
                    <option value="">{tx('Select', 'Selectionner')}</option>
                    <option value="France Travail">{tx('France Travail', 'France Travail')}</option>
                    <option value="Mission Locale">{tx('Local Mission', 'Mission Locale')}</option>
                    <option value="Other">{tx('Other', 'Autre')}</option>
                  </select>
                </div>
                {form.org_type === 'Other' && (
                  <div className="form-group"><label>{tx('Specify', 'Précisez')}</label>
                    <input value={form.org_type_other} onChange={(e) => setForm((f) => ({ ...f, org_type_other: e.target.value }))} />
                  </div>
                )}
                <div className="form-group"><label>{tx('Town', 'Ville')}</label>
                  <input value={form.org_town} onChange={(e) => setForm((f) => ({ ...f, org_town: e.target.value }))} />
                </div>
                <div className="form-group"><label>{tx('Department', 'Département')}</label>
                  <input value={form.org_department} onChange={(e) => setForm((f) => ({ ...f, org_department: e.target.value }))} />
                </div>
                <div className="form-group"><label>{tx('Advisor Name', 'Nom de votre conseiller')}</label>
                  <input value={form.org_advisor_name} onChange={(e) => setForm((f) => ({ ...f, org_advisor_name: e.target.value }))} />
                </div>
                <div className="form-group"><label>{tx('Advisor Email', 'Email du conseiller')}</label>
                  <input type="email" value={form.org_advisor_email} onChange={(e) => setForm((f) => ({ ...f, org_advisor_email: e.target.value }))} />
                </div>
                <div className="form-group"><label>{tx('Advisor Phone', 'Téléphone du conseiller')}</label>
                  <PhoneInput
                    international
                    defaultCountry="FR"
                    value={
                      form.org_advisor_phone
                        ? `${form.org_advisor_phone_country_code || '+33'}${form.org_advisor_phone}`
                        : undefined
                    }
                    onChange={(value) => {
                      if (!value) {
                        setForm((f) => ({ ...f, org_advisor_phone: '', org_advisor_phone_country_code: '' }));
                        return;
                      }
                      try {
                        const parsed = parsePhoneNumber(value);
                        if (parsed?.countryCallingCode && parsed?.nationalNumber) {
                          setForm((f) => ({
                            ...f,
                            org_advisor_phone: String(parsed.nationalNumber),
                            org_advisor_phone_country_code: `+${parsed.countryCallingCode}`
                          }));
                          return;
                        }
                      } catch {}
                      setForm((f) => ({
                        ...f,
                        org_advisor_phone: value.replace(/^\+/, ''),
                        org_advisor_phone_country_code: ''
                      }));
                    }}
                    placeholder="+33 06 12 34 56 78"
                  />
                </div>
                <div className="form-group"><label>{tx('Registered with France Travail', 'Inscrit(e) à France Travail')}</label>
                  <select value={form.org_registered_france_travail} onChange={(e) => setForm((f) => ({ ...f, org_registered_france_travail: e.target.value }))}>
                    <option value="">{tx('Select', 'Selectionner')}</option>
                    <option value="Yes">{tx('Yes', 'Oui')}</option>
                    <option value="No">{tx('No', 'Non')}</option>
                  </select>
                </div>
                <div className="form-group"><label>{tx('Registration Date', 'Date d\u2019inscription')}</label>
                  <input type="date" value={form.org_registration_date} onChange={(e) => setForm((f) => ({ ...f, org_registration_date: e.target.value }))} />
                </div>
                <div className="form-group"><label>{tx('Identification Number', 'Numéro d\u2019identifiant')}</label>
                  <input value={form.org_identification_number} onChange={(e) => setForm((f) => ({ ...f, org_identification_number: e.target.value }))} />
                </div>
                <div className="form-group"><label>{tx('Compensated by France Travail', 'Indemnisé(e) par France Travail')}</label>
                  <select value={form.org_compensated_france_travail} onChange={(e) => setForm((f) => ({ ...f, org_compensated_france_travail: e.target.value }))}>
                    <option value="">{tx('Select', 'Selectionner')}</option>
                    <option value="Yes">{tx('Yes', 'Oui')}</option>
                    <option value="No">{tx('No', 'Non')}</option>
                    <option value="Coming soon">{tx('Coming soon', 'À venir')}</option>
                  </select>
                </div>
                <div className="form-group"><label>{tx('Until what date', 'Jusqu\u2019à quelle date')}</label>
                  <input type="date" value={form.org_compensated_until_date} onChange={(e) => setForm((f) => ({ ...f, org_compensated_until_date: e.target.value }))} />
                </div>
              </>
            )}

            {/* France-only: Table sections */}
            {isFrance && (
              <div className="form-group full-width" style={{ marginTop: 16 }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>Studies Followed</h4>
                <div className="table-responsive">
                  <table className="table" style={{ fontSize: 13 }}>
                    <thead><tr><th style={{ width: 110 }}>Level</th><th style={{ width: 80 }}>Year</th><th>Establishment</th><th>Title of Degree / Result</th><th>Comments</th></tr></thead>
                    <tbody>
                      {(form.studies_followed || []).map((row, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 600 }}>{row.level}</td>
                          <td><input value={row.year} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.studies_followed || [])]; arr[idx] = { ...arr[idx], year: v }; return { ...f, studies_followed: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.establishment} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.studies_followed || [])]; arr[idx] = { ...arr[idx], establishment: v }; return { ...f, studies_followed: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.degree_title} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.studies_followed || [])]; arr[idx] = { ...arr[idx], degree_title: v }; return { ...f, studies_followed: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.comments} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.studies_followed || [])]; arr[idx] = { ...arr[idx], comments: v }; return { ...f, studies_followed: arr }; }); }} style={{ width: '100%' }} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isFrance && (
              <div className="form-group full-width" style={{ marginTop: 16 }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>Modern Languages</h4>
                <div className="table-responsive">
                  <table className="table" style={{ fontSize: 13 }}>
                    <thead><tr><th>Language</th><th>Written</th><th>Oral</th><th>Duration</th></tr></thead>
                    <tbody>
                      {(form.modern_languages || []).map((row, idx) => (
                        <tr key={idx}>
                          <td><input value={row.language} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.modern_languages || [])]; arr[idx] = { ...arr[idx], language: v }; return { ...f, modern_languages: arr }; }); }} placeholder={`${idx + 1}${idx === 0 ? 'st' : idx === 1 ? 'nd' : 'rd'} language`} style={{ width: '100%' }} /></td>
                          <td><select value={row.written} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.modern_languages || [])]; arr[idx] = { ...arr[idx], written: v }; return { ...f, modern_languages: arr }; }); }}><option value="">-</option><option value="Weak">{tx('Weak', 'Faible')}</option><option value="Medium">{tx('Medium', 'Moyen')}</option><option value="Strong">{tx('Strong', 'Fort')}</option></select></td>
                          <td><select value={row.oral} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.modern_languages || [])]; arr[idx] = { ...arr[idx], oral: v }; return { ...f, modern_languages: arr }; }); }}><option value="">-</option><option value="Weak">{tx('Weak', 'Faible')}</option><option value="Medium">{tx('Medium', 'Moyen')}</option><option value="Strong">{tx('Strong', 'Fort')}</option></select></td>
                          <td><input value={row.duration} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.modern_languages || [])]; arr[idx] = { ...arr[idx], duration: v }; return { ...f, modern_languages: arr }; }); }} style={{ width: '100%' }} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isFrance && (
              <div className="form-group full-width" style={{ marginTop: 16 }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>Experience of Stays Abroad</h4>
                <div className="table-responsive">
                  <table className="table" style={{ fontSize: 13 }}>
                    <thead><tr><th>Country</th><th>Duration</th><th style={{ width: 80 }}>Year</th><th>Activities</th></tr></thead>
                    <tbody>
                      {(form.stays_abroad || []).map((row, idx) => (
                        <tr key={idx}>
                          <td><input value={row.country} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.stays_abroad || [])]; arr[idx] = { ...arr[idx], country: v }; return { ...f, stays_abroad: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.duration} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.stays_abroad || [])]; arr[idx] = { ...arr[idx], duration: v }; return { ...f, stays_abroad: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.year} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.stays_abroad || [])]; arr[idx] = { ...arr[idx], year: v }; return { ...f, stays_abroad: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.activities} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.stays_abroad || [])]; arr[idx] = { ...arr[idx], activities: v }; return { ...f, stays_abroad: arr }; }); }} style={{ width: '100%' }} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isFrance && (
              <div className="form-group full-width" style={{ marginTop: 16 }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>Jobs, Seasonal Work, Internships</h4>
                <div className="table-responsive">
                  <table className="table" style={{ fontSize: 13 }}>
                    <thead><tr><th>Companies (name and city)</th><th>Duration and year</th><th>Missions carried out</th></tr></thead>
                    <tbody>
                      {(form.work_internships || []).map((row, idx) => (
                        <tr key={idx}>
                          <td><input value={row.company} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.work_internships || [])]; arr[idx] = { ...arr[idx], company: v }; return { ...f, work_internships: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.duration_year} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.work_internships || [])]; arr[idx] = { ...arr[idx], duration_year: v }; return { ...f, work_internships: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.missions} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.work_internships || [])]; arr[idx] = { ...arr[idx], missions: v }; return { ...f, work_internships: arr }; }); }} style={{ width: '100%' }} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isFrance && (
              <div className="form-group full-width" style={{ marginTop: 16 }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>Cultural, Sports and Associative Activities</h4>
                <div className="table-responsive">
                  <table className="table" style={{ fontSize: 13 }}>
                    <thead><tr><th>Nature of the activity</th><th>Number of years</th><th>Level</th></tr></thead>
                    <tbody>
                      {(form.cultural_activities || []).map((row, idx) => (
                        <tr key={idx}>
                          <td><input value={row.activity} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.cultural_activities || [])]; arr[idx] = { ...arr[idx], activity: v }; return { ...f, cultural_activities: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.years} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.cultural_activities || [])]; arr[idx] = { ...arr[idx], years: v }; return { ...f, cultural_activities: arr }; }); }} style={{ width: '100%' }} /></td>
                          <td><input value={row.level} onChange={(e) => { const v = e.target.value; setForm(f => { const arr = [...(f.cultural_activities || [])]; arr[idx] = { ...arr[idx], level: v }; return { ...f, cultural_activities: arr }; }); }} style={{ width: '100%' }} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="form-group full-width">
              <label>Required Documents *</label>
              {requiredDocuments.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No required documents configured.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                  {requiredDocuments.map((docName) => {
                    const key = String(docName);
                    const inputId = `required-doc-${key.replace(/\s+/g, '-').toLowerCase()}`;
                    const selectedFile = requiredDocFiles[key];
                    return (
                      <div
                        key={key}
                        style={{
                          border: '1px solid var(--border-color)',
                          borderRadius: 8,
                          padding: 12,
                          background: 'var(--card-bg)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          minWidth: 0
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{key}</div>
                        <input
                          id={inputId}
                          type="file"
                          accept=".pdf,.jpg,.jpeg"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            if (file) {
                              const nextName = String(file.name || '').trim().toLowerCase();
                              const duplicateKey = Object.entries(requiredDocFiles || {}).find(([k, f]) => {
                                if (k === key) return false;
                                const n = String(f?.name || '').trim().toLowerCase();
                                return n && nextName && n === nextName;
                              })?.[0];
                              if (duplicateKey) {
                                toast.error(`This file is already selected for "${duplicateKey}"`);
                                e.target.value = '';
                                return;
                              }
                              const sizeErr = getDocumentFileSizeError(file);
                              if (sizeErr) {
                                toast.error(sizeErr);
                                e.target.value = '';
                                return;
                              }
                            }
                            setRequiredDocFiles((prev) => ({ ...prev, [key]: file }));
                            setRequiredDocVerification((prev) => ({ ...prev, [key]: '' }));
                            if (file && passportDocKeys.includes(key)) {
                              console.log('[Passport Verify][IntakeForm] File selected on passport row:', {
                                document_type: key,
                                file_name: file?.name,
                                file_type: file?.type,
                                file_size: file?.size
                              });
                              verifyPassportImmediately(key, file);
                            }
                          }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <label htmlFor={inputId} className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
                            {selectedFile ? 'Replace File' : 'Choose File'}
                          </label>
                          <span style={{ color: 'var(--text-muted)', fontSize: 12, minWidth: 0 }}>
                            {selectedFile ? selectedFile.name : 'No file selected'}
                          </span>
                        </div>
                        {requiredDocVerification[key] === 'verified' ? (
                          <div style={{ fontSize: 12, color: 'var(--success)', fontWeight: 700 }}>
                            {t('students.intakePassportVerified', { lng, defaultValue: 'Verified' })}
                          </div>
                        ) : null}
                        {requiredDocVerifying[key] ? (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>
                            {t('students.intakePassportVerifying', { lng, defaultValue: 'Verifying passport document...' })}
                          </div>
                        ) : null}
                        {requiredDocVerification[key] === 'not_verified' ? (
                          <div style={{ fontSize: 12, color: 'var(--danger)', fontWeight: 700 }}>
                            {t('students.intakePassportNotVerified', { lng, defaultValue: 'Not verified' })}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {submissionAttempted && submitDisabled && (missingAcademicSelection || missingRequiredDocs.length > 0) ? (
            <div
              className="card"
              style={{
                marginTop: 16,
                padding: 12,
                borderColor: 'var(--danger, #b91c1c)',
                background: 'rgba(185, 28, 28, 0.06)'
              }}
              role="alert"
            >
              <strong style={{ display: 'block', marginBottom: 8 }}>
                {t('students.intakeValidationIssuesTitle', { lng })}
              </strong>
              {missingAcademicSelection ? (
                <p style={{ margin: '0 0 8px', fontSize: 14 }}>{t('students.intakeMissingAcademic', { lng })}</p>
              ) : null}
              {missingRequiredDocs.length > 0 ? (
                <p style={{ margin: 0, fontSize: 14 }}>
                  {t('students.intakeMissingDocumentsList', {
                    lng,
                    list: missingRequiredDocs.map((d) => String(d)).join(', ')
                  })}
                </p>
              ) : null}
            </div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-primary" type="submit" disabled={saving || submitDisabled}>
              {saving ? tx('Submitting...', 'Soumission...') : tx('Submit', 'Soumettre')}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}
